/**
 * Google Analytics 4 and Google Tag Manager utility functions
 * Provides centralized analytics event tracking with error handling
 */

import type {
  AnalyticsEventData,
  PropertyViewEvent,
  SearchEvent,
  ContactFormEvent,
  UserInteractionEvent,
  PageViewEvent,
  GTMEvent,
} from "@/types/analytics";

/**
 * Initialize Google Analytics
 * @param measurementId - GA4 measurement ID
 */
export const initGA = (measurementId: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments as any);
    };
    window.gtag("js", new Date());
    window.gtag("config", measurementId, {
      anonymize_ip: true,
      cookie_flags: "SameSite=None;Secure",
    });
  } catch (error) {
    console.error("Failed to initialize Google Analytics:", error);
  }
};

/**
 * Track a page view
 * @param data - Page view event data
 */
export const trackPageView = (data: PageViewEvent): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", "page_view", {
      page_title: data.page_title,
      page_path: data.page_path,
      page_location: data.page_location,
    });
  } catch (error) {
    console.error("Failed to track page view:", error);
  }
};

/**
 * Track a property view event
 * @param data - Property view event data
 */
export const trackPropertyView = (
  data: Omit<PropertyViewEvent, "event_category">,
): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", "view_item", {
      event_category: "property",
      event_label: data.property_id,
      property_id: data.property_id,
      property_type: data.property_type,
      property_price: data.property_price,
      property_location: data.property_location,
      property_bedrooms: data.property_bedrooms,
      value: data.property_price,
    });
  } catch (error) {
    console.error("Failed to track property view:", error);
  }
};

/**
 * Track a search event
 * @param data - Search event data
 */
export const trackSearch = (
  data: Omit<SearchEvent, "event_category">,
): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", "search", {
      event_category: "search",
      event_label: data.search_term,
      search_term: data.search_term,
      search_type: data.search_type,
      search_location: data.search_location,
      results_count: data.results_count,
    });
  } catch (error) {
    console.error("Failed to track search:", error);
  }
};

/**
 * Track a contact form submission
 * @param data - Contact form event data
 */
export const trackContactForm = (
  data: Omit<ContactFormEvent, "event_category">,
): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", data.success ? "form_submit" : "form_error", {
      event_category: "contact",
      event_label: data.form_type,
      form_type: data.form_type,
      property_id: data.property_id,
      success: data.success,
    });
  } catch (error) {
    console.error("Failed to track contact form:", error);
  }
};

/**
 * Track a user interaction
 * @param data - User interaction event data
 */
export const trackUserInteraction = (
  data: Omit<UserInteractionEvent, "event_category">,
): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", data.interaction_type, {
      event_category: "engagement",
      event_label: data.element_id || data.element_text,
      interaction_type: data.interaction_type,
      element_id: data.element_id,
      element_text: data.element_text,
    });
  } catch (error) {
    console.error("Failed to track user interaction:", error);
  }
};

/**
 * Track a custom event
 * @param eventName - Event name
 * @param eventData - Event data
 */
export const trackEvent = (
  eventName: string,
  eventData?: AnalyticsEventData,
): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("event", eventName, eventData || {});
  } catch (error) {
    console.error("Failed to track custom event:", error);
  }
};

/**
 * Push data to GTM data layer
 * @param data - GTM event data
 */
export const pushToDataLayer = (data: GTMEvent): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(data);
  } catch (error) {
    console.error("Failed to push to data layer:", error);
  }
};

/**
 * Set user properties
 * @param userId - User ID
 * @param properties - Additional user properties
 */
export const setUserProperties = (
  userId: string,
  properties?: Record<string, unknown>,
): void => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  try {
    window.gtag("set", {
      user_id: userId,
      ...properties,
    });
  } catch (error) {
    console.error("Failed to set user properties:", error);
  }
};
